/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useState } from 'react'

// Services
import {
  useBetterIdLookup,
  usePaginatedCompanySearch,
  useSearchBetterIdWithMetaData,
} from 'lib/apollo/hooks'

// Utils
import { getAssetTypeCaps } from 'lib/utils/getAssetType'
import { groupParentWithChildren } from 'lib/utils/groupCompanies'

// Types
import {
  ManualAddFormProps,
  ManualDrawerProps,
  ScannedField,
} from './ManualAddForm.types'
import { BetterIdAsset, ManualInputFormData, Option } from 'common/types'
import { SearchType } from './ManualAddForm.types'

// Other
import { debounce } from 'lodash'
import { useUndocumentedAssetsContext } from 'lib/context/UndocumentedAssetsContext'
import { useCortexDecoderContext } from 'lib/scanner/compatibility'
import { useAssetType } from 'lib/context/AssetTypeContext/AssetTypeContext'
import { useUser } from 'app/User'
import { parseAssetIdentifiers } from 'lib/utils/ParseAssetIdentifiers/parseAssetIdentifiers'
import { useLocation, useNavigate } from 'react-router-dom'
import { parseUdi } from 'lib/utils/codeIdentifierParser'
import Cookies from 'js-cookie'

const useManualAddFormLogic = ({
  assetFormData,
  handleSubmit,
  setIsTrayScannerOpen,
  registerTrayScanHandler,
  setManualAssetInputFormData,
  onSearchWithTypeChange,
}: ManualAddFormProps) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { setCurrentScan, currentScan, resetScan } = useCortexDecoderContext()

  const [searchType, setSearchType] = useState<
    'UDI' | 'Device Description/Catalog Number'
  >('Device Description/Catalog Number')
  const isSearchWithUDI = searchType === 'UDI'
  const isSearchWithOther = searchType === 'Device Description/Catalog Number'
  const [codeValue, setCodeValue] = useState<string>()
  const [selectedCompany, setSelectedCompany] = useState<Option>()
  const [selectedAssetOption, setSelectedAssetOption] = useState<Option>()
  const [udi, setUDI] = useState<string>()
  const [isDrawerOpen, setIsDrawerOpen] = useState<ManualDrawerProps>({
    isOpen: false,
  })
  const [scannedField, setScannedField] = useState<ScannedField>()
  const [hasQuantity, setHasQuantity] = useState(false)
  const [isRefetching, setIsRefetching] = useState(false)
  const [selectedAsset, setSelectedAsset] = useState<BetterIdAsset>()
  const [isCatalogScanned, setIsCatalogScanned] = useState(false)
  const { isLoadingUndocumentedList } = useUndocumentedAssetsContext()
  const { assetType } = useAssetType()

  const canSave =
    !!assetFormData.bidAssetId &&
    !!assetFormData.deviceId &&
    (!assetFormData.hasLotSerial
      ? !!assetFormData.lotBatch || !!assetFormData.serialNumber
      : true)

  const customPlaceholder = () => {
    if (!isDrawerOpen.isOpen) return

    const placeholders = {
      company: 'Search for a company',
      description: 'Search with product description',
      catalogNumber: 'Search with catalog number',
      UDI: 'Search with UDI',
      default: '',
    }

    return placeholders[isDrawerOpen.type as SearchType]
  }

  const { isRep, user } = useUser()
  const repBidCompanyId = isRep ? user?.bidCompanyId || null : null
  const repCompanyIds = isRep
    ? [
        user?.bidCompanyId as number,
        ...(user?.parentCompanyId ? [user.parentCompanyId] : []),
        ...((user?.siblingCompaniesIds as number[]) ?? []),
        ...((user?.subsidiariesCompaniesIds as number[]) ?? []),
      ]
    : []

  const { isRfMultipackConsumable, isSpongeConsumable } = parseAssetIdentifiers(
    {
      deviceDescription: selectedAsset?.deviceDescription,
      deviceCount: selectedAsset?.deviceCount ?? 0,
      idType: selectedAsset?.secondaryDeviceIdType,
      assetType: getAssetTypeCaps(assetType),
    }
  )

  const isSponge = isRfMultipackConsumable || isSpongeConsumable

  // Services
  const [
    getCompanies,
    {
      loading: isLoadingCompanies,
      data: rawCompaniesData,
      fetchMore: fetchMoreCompanies,
    },
  ] = usePaginatedCompanySearch()

  const [
    search,
    {
      loading: isLoadingSearch,
      data: rawSearchData,
      fetchMore: fetchMoreProducts,
    },
  ] = useSearchBetterIdWithMetaData()

  const [searchUDI, { loading: isLoadingSearchUDI, data: rawSearchUDIData }] =
    useBetterIdLookup()

  const companies =
    groupParentWithChildren(
      rawCompaniesData?.companySearchPaginated?.companies || []
    ) || ([] as Option[])

  const searchData =
    rawSearchData?.betterIdLookupByModelOrDD.products.map((item) => ({
      id: item.id,
      name: `${item.deviceDescription} (CN: ${
        item.catalogNumber || item.versionModelNumber
      })` as string,
      isMultipack: item.deviceCount > 1,
    })) || []

  const searchUDIOptions = repBidCompanyId
    ? (rawSearchUDIData?.betterIdLookup.assets
        .filter((item) => repCompanyIds.includes(item.companyId as number))
        .map((item) => ({
          id: item.id,
          name: `${item.deviceDescription} (CN: ${
            item.catalogNumber || item.versionModelNumber
          })`,
        })) as Option[]) || []
    : (rawSearchUDIData?.betterIdLookup.assets.map((item) => ({
        id: item.id,
        name: `${item.deviceDescription} (CN: ${
          item.catalogNumber || item.versionModelNumber
        })`,
      })) as Option[]) || []

  const isLoadingOptions =
    isLoadingCompanies || isLoadingSearch || isLoadingSearchUDI

  const CUSTOM_OPTION_TYPES: { [key in SearchType]: Option[] } = {
    company: companies,
    description: searchData,
    catalogNumber: searchData,
    UDI: searchUDIOptions,
    default: [],
  }

  const customOptions = (): Option[] => {
    return CUSTOM_OPTION_TYPES[isDrawerOpen.type as SearchType]
  }

  const handleCloseDrawer = () => {
    setIsDrawerOpen({ isOpen: false })
    setCodeValue(undefined)
    setIsCatalogScanned(false)
  }

  const debouncedSetSearchValue = useCallback(
    debounce((value: string) => {
      switch (isDrawerOpen.type) {
        case 'company':
          getCompanies({
            variables: {
              data: {
                name: value,
                limit: 20,
                ...(isRep && { companiesIds: repCompanyIds }),
              },
            },
          })
          break
        case 'description':
          search({
            variables: {
              data: {
                assetType: assetType,
                companyId:
                  (selectedCompany?.id as number) || assetFormData.bidCompanyId,
                deviceDescription: value,
                limit: 15,
              },
            },
          })
          break
        case 'catalogNumber':
          search({
            variables: {
              data: {
                assetType: assetType,
                companyId:
                  (selectedCompany?.id as number) || assetFormData.bidCompanyId,
                versionModelNumber: value,
                limit: 15,
                exactMatch: isCatalogScanned,
              },
            },
          })
          break
        case 'UDI':
          searchUDI({
            variables: {
              lookupValue: value,
              assetType: assetType,
            },
          })
          setUDI(value)
          break
        default:
          break
      }
    }, 300),
    [isDrawerOpen.type]
  )

  const updateFormData = (update: Partial<ManualInputFormData>) => {
    setManualAssetInputFormData((prev) => ({
      ...prev,
      ...update,
    }))
  }

  const handleSearchSelect = (option: Option) => {
    switch (isDrawerOpen.type) {
      case 'company':
        setSelectedCompany(option)
        updateFormData({
          companyName: option.name,
          bidCompanyId: option?.id as number,
          deviceDescription: undefined,
          catalogNumber: undefined,
          versionModelNumber: undefined,
        })
        break
      case 'description':
        setSelectedAssetOption(option)
        updateFormData({
          deviceDescription: option.name,
        })
        break
      case 'catalogNumber':
        setSelectedAssetOption(option)
        updateFormData({
          catalogNumber: option.name,
        })
        break
      case 'UDI':
        setSelectedAssetOption(option)
        updateFormData({
          deviceDescription: option.name,
        })
        break
      default:
        break
    }
    setIsDrawerOpen({ isOpen: false })
  }

  const handleInfiniteScroll = useCallback(() => {
    if (isDrawerOpen?.type === 'company') {
      const companies = rawCompaniesData?.companySearchPaginated?.companies
      if (!companies) return

      const totalCount = rawCompaniesData.companySearchPaginated.totalCount

      if (companies.length >= totalCount) return

      setIsRefetching(true)

      fetchMoreCompanies({
        variables: {
          data: {
            name: codeValue,
            limit: 15,
            skip: companies.length,
          },
        },
        updateQuery(prev, { fetchMoreResult }) {
          if (!fetchMoreResult) {
            setIsRefetching(false)
            return prev
          }

          const newItems =
            fetchMoreResult.companySearchPaginated.companies.filter(
              (newItem) =>
                !prev.companySearchPaginated.companies.some(
                  (prevItem) => prevItem.id === newItem.id
                )
            )

          setIsRefetching(false)

          return {
            companySearchPaginated: {
              ...prev.companySearchPaginated,
              companies: [
                ...prev.companySearchPaginated.companies,
                ...newItems,
              ],
              totalCount,
            },
          }
        },
      }).catch(() => setIsRefetching(false)) // Ensure state reset on error​
    }
    if (
      isDrawerOpen.type === 'description' ||
      isDrawerOpen.type === 'catalogNumber'
    ) {
      if (!rawSearchData) return

      const totalCount = rawSearchData?.betterIdLookupByModelOrDD?.totalCount
      const products = rawSearchData.betterIdLookupByModelOrDD?.products || []
      const productsLength = products.length

      if (productsLength >= totalCount) return

      setIsRefetching(true)

      fetchMoreProducts({
        variables: {
          data: {
            companyId:
              (selectedCompany?.id as number) || assetFormData.bidCompanyId,
            deviceDescription: codeValue,
            limit: 20,
            cursor:
              rawSearchData.betterIdLookupByModelOrDD?.products[
                rawSearchData.betterIdLookupByModelOrDD?.products.length - 1
              ].id,
          },
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev

          const newItems =
            fetchMoreResult.betterIdLookupByModelOrDD.products.filter(
              (newItem) =>
                !prev.betterIdLookupByModelOrDD.products.some(
                  (prevItem) => prevItem.id === newItem.id
                )
            )

          setIsRefetching(false)

          return {
            betterIdLookupByModelOrDD: {
              products: [
                ...prev.betterIdLookupByModelOrDD.products,
                ...newItems,
              ],
              totalCount: prev.betterIdLookupByModelOrDD.totalCount,
            },
          }
        },
      }).catch(() => setIsRefetching(false))
    }
  }, [
    codeValue,
    isDrawerOpen,
    rawSearchData,
    rawCompaniesData,
    fetchMoreProducts,
    fetchMoreCompanies,
  ])

  const handleOpenScanner = (field: ScannedField) => {
    setScannedField(field)
    setIsTrayScannerOpen && setIsTrayScannerOpen(true)
  }

  const handleFormSubmit = (e: any) => {
    e.preventDefault()
    handleSubmit(e)
  }

  const handleChangeSearchType = (
    event: React.MouseEvent<HTMLElement>,
    nextView: 'UDI' | 'Device Description/Catalog Number'
  ) => {
    setSearchType(nextView)
    onSearchWithTypeChange?.(nextView === 'UDI')
  }

  const handleOpenProductRequestForm = () => {
    const basePath = window.location.pathname.split('/asset')[0]

    const scannedCode = location.state as { scannedCode: string }

    const { deviceId, expirationDate, lotBatch, serialNumber } = parseUdi(
      scannedCode?.scannedCode || ''
    )

    Cookies.remove('productRequestData')
    navigate(`${basePath}/asset/create`, {
      state: {
        deviceDescription:
          isDrawerOpen.type === 'description' ? codeValue : undefined,
        catalogNumber:
          isDrawerOpen.type === 'catalogNumber' ? codeValue : undefined,
        udi: isDrawerOpen.type === 'UDI' ? codeValue : undefined,
        companyName: isDrawerOpen.type === 'company' ? codeValue : undefined,
        selectedCompany,
        ...(scannedCode
          ? {
              deviceId: deviceId || undefined,
              expirationDate: expirationDate || undefined,
              lotBatch: lotBatch || undefined,
              serialNumber: serialNumber || undefined,
            }
          : {}),
      },
    })
  }

  useEffect(() => {
    if (codeValue) {
      debouncedSetSearchValue(codeValue)
    }
    return () => {
      debouncedSetSearchValue.cancel()
    }
  }, [codeValue, debouncedSetSearchValue])

  useEffect(() => {
    const setFormData = (asset?: BetterIdAsset) => {
      if (!asset) {
        setManualAssetInputFormData((prev) => ({
          ...prev,
          assetType:
            assetType === 'EXPLANTED'
              ? 'non-biological'
              : getAssetTypeCaps(assetType),
          isExplanted: assetType === 'EXPLANTED',
          bidAssetId: assetType === 'EXPLANTED' ? 0 : undefined,
          deviceCategoryDetails: undefined,
          spongeCount: undefined,
        }))
        return
      }
      setSelectedAsset(asset)
      setManualAssetInputFormData((prev) => ({
        ...prev,
        catalogNumber: asset.catalogNumber || asset.versionModelNumber,
        assetType: getAssetTypeCaps(assetType),
        deviceCount: asset.deviceCount,
        deviceDescription: asset.deviceDescription,
        versionModelNumber: asset.versionModelNumber,
        bidCompanyId: asset.companyId,
        bidAssetId: asset.id,
        chargeable: asset.chargeable,
        cost: asset.cost,
        deviceId: asset.deviceId,
        fromAssetInstanceId: asset.fromAssetInstanceId,
        gmdnPTDefinition: asset.gmdnPTDefinition,
        id: asset.id,
        issuingAgency: asset.issuingAgency,
        pkgQuantity: asset.pkgQuantity,
        secondaryDeviceId: asset.secondaryDeviceId,
        secondaryDeviceIdPkgQuantity: asset.secondaryDeviceIdPkgQuantity,
        secondaryDeviceIdPkgType: asset.secondaryDeviceIdPkgType,
        secondaryDeviceIdType: asset.secondaryDeviceIdType,
        sizeString: asset.sizeString,
        sizeText: asset.sizeText,
        sourceId: asset.sourceId,
        udi,
        isManualAddition: true,
        companyName: asset.company.name,
        parentCompany: (asset.company.parentCompany?.name as string) || null,
        parentCompanyId: (asset.company.parentCompanyId as number) || null,
        quantity: assetFormData.quantity as number,
        ...(isSearchWithUDI && {
          companyName: asset.company.name,
          companyId: asset.company.id,
          serialNumber:
            rawSearchUDIData?.betterIdLookup.productionIdentifier
              .serialNumber || undefined,
          lotBatch:
            rawSearchUDIData?.betterIdLookup.productionIdentifier.lotBatch ||
            undefined,
          expirationDate:
            rawSearchUDIData?.betterIdLookup.productionIdentifier
              .expirationDate || undefined,
          manufacturingDate:
            rawSearchUDIData?.betterIdLookup.productionIdentifier
              .manufacturingDate || undefined,
        }),
        isExplanted: assetType === 'EXPLANTED',
        deviceCategoryDetails: asset?.deviceCategoryDetails,
        spongeCount: asset?.spongeCount,
        isProductInGUDID: asset?.isProductInGUDID ?? true,
      }))
    }

    let asset = {} as BetterIdAsset | undefined

    if (isSearchWithOther) {
      asset = rawSearchData?.betterIdLookupByModelOrDD.products.find(
        (item) => item.id === selectedAssetOption?.id
      )
      setFormData(asset)
    }

    if (isSearchWithUDI) {
      asset = rawSearchUDIData?.betterIdLookup.assets.find(
        (item) => item.id === selectedAssetOption?.id
      )
      setFormData(asset)
    }

    if (assetType === 'IMPLANTABLE BIOLOGIC') {
      setHasQuantity(false)
    } else {
      setHasQuantity(true)
    }
  }, [rawSearchData, rawSearchUDIData, selectedAssetOption, assetType])

  useEffect(() => {
    if (selectedCompany) {
      search({
        variables: {
          data: {
            assetType: assetType,
            companyId: selectedCompany.id as number,
            limit: 20,
          },
        },
      })
    }
  }, [selectedCompany])

  useEffect(() => {
    if (!currentScan) return
    setCurrentScan(undefined)

    if (scannedField === 'lot') {
      updateFormData({
        lotBatch: currentScan.barcodeData,
      })
    }
    if (scannedField === 'serial') {
      updateFormData({
        serialNumber: currentScan.barcodeData,
      })
    }
    if (scannedField === 'catalog') {
      setCodeValue(currentScan.barcodeData)
      setIsDrawerOpen({ isOpen: true, type: 'catalogNumber' })
      setIsCatalogScanned(true)
    }

    resetScan()
    setIsTrayScannerOpen(false)
  }, [registerTrayScanHandler, currentScan])

  useEffect(() => {
    if (repBidCompanyId) {
      getCompanies({
        variables: {
          data: {
            limit: 20,
            ...(isRep && {
              companiesIds: [user?.bidCompanyId as number],
            }),
          },
        },
      })
    }
  }, [])

  // Add mock scanner for testing
  useEffect(() => {
    if (window && process.env.REACT_APP_NODE_ENV !== 'production') {
      window.mockScan = (scan: string) => {
        if (scannedField === 'lot') {
          updateFormData({
            lotBatch: scan,
          })
        } else if (scannedField === 'serial') {
          updateFormData({
            serialNumber: scan,
          })
        } else if (scannedField === 'catalog') {
          setCodeValue(scan)
          setIsDrawerOpen({ isOpen: true, type: 'catalogNumber' })
          setIsCatalogScanned(true)
        }
        setCurrentScan(undefined)
      }
    }
  }, [registerTrayScanHandler])

  const showCreateRequest =
    !isLoadingSearch &&
    !isLoadingCompanies &&
    !isLoadingSearchUDI &&
    customOptions()?.length === 0 &&
    Number(codeValue?.trim().length) > 1 &&
    isDrawerOpen.type === 'company'
      ? rawCompaniesData?.companySearchPaginated.companies.length === 0
      : rawSearchData?.betterIdLookupByModelOrDD.products.length === 0 ||
        rawSearchUDIData?.betterIdLookup.assets.length === 0

  const productRequestLabel =
    isDrawerOpen.type === 'company'
      ? 'This manufacturer does not exist in MedGEO. Do you want to submit a product request?'
      : "This product doesn't exist in MedGEO. Do you want to submit a product request?"

  return {
    canSave,
    isSponge,
    codeValue,
    searchType,
    hasQuantity,
    isDrawerOpen,
    isRefetching,
    selectedAsset,
    repBidCompanyId,
    isSearchWithUDI,
    selectedCompany,
    isLoadingOptions,
    isSearchWithOther,
    productRequestLabel,
    isLoadingUndocumentedList,
    setSearchType,
    setCodeValue,
    customOptions,
    setIsDrawerOpen,
    handleFormSubmit,
    customPlaceholder,
    handleOpenScanner,
    handleCloseDrawer,
    handleSearchSelect,
    handleInfiniteScroll,
    handleChangeSearchType,
    handleOpenProductRequestForm,
    showCreateRequest,
  }
}

export default useManualAddFormLogic
